import { fabric } from "fabric";

/**
 * Typefaces offered for text in the design editor. The web player loads the same
 * Google Fonts (public/web-player.html, TEXT_FONTS_CSS) so layouts render the same on screens.
 * Keep the two lists in sync.
 */
export const TEXT_FONTS = [
  { label: "Arial", family: "Arial" },
  { label: "Inter", family: "Inter" },
  { label: "Roboto", family: "Roboto" },
  { label: "Open Sans", family: "Open Sans" },
  { label: "Lato", family: "Lato" },
  { label: "Montserrat", family: "Montserrat" },
  { label: "Poppins", family: "Poppins" },
  { label: "Oswald", family: "Oswald" },
  { label: "Bebas Neue", family: "Bebas Neue" },
  { label: "Playfair Display", family: "Playfair Display" },
  { label: "Merriweather", family: "Merriweather" },
];

export const TEXT_FONTS_CSS =
  "https://fonts.googleapis.com/css2?family=Inter:wght@400;700&family=Roboto:wght@400;700" +
  "&family=Open+Sans:wght@400;700&family=Lato:wght@400;700&family=Montserrat:wght@400;700" +
  "&family=Poppins:wght@400;700&family=Oswald:wght@400;700&family=Bebas+Neue" +
  "&family=Playfair+Display:wght@400;700&family=Merriweather:wght@400;700&display=swap";

/** Inject the font stylesheet once. */
export function ensureTextFontsLoaded() {
  if (typeof document === "undefined" || document.getElementById("de-text-fonts")) return;
  const link = document.createElement("link");
  link.id = "de-text-fonts";
  link.rel = "stylesheet";
  link.href = TEXT_FONTS_CSS;
  document.head.appendChild(link);
}

/** Resolve once the given family (normal + bold) is usable, or after a short timeout. */
export function loadTextFont(family) {
  if (typeof document === "undefined" || !document.fonts || !family) return Promise.resolve();
  const q = `"${family}"`;
  const load = Promise.all([document.fonts.load(`40px ${q}`), document.fonts.load(`bold 40px ${q}`)]);
  const timeout = new Promise((resolve) => setTimeout(resolve, 2500));
  return Promise.race([load, timeout]).catch(() => {});
}

/** First family name of a CSS font-family value, unquoted. */
export function primaryFontFamily(value) {
  if (!value || typeof value !== "string") return "Arial";
  return value.split(",")[0].trim().replace(/^["']|["']$/g, "");
}

/**
 * Fabric measures and paints text with whatever font is ready at load time. When a
 * layout uses a web font, re-measure and repaint its texts once that font arrives.
 */
export function rerenderTextsWhenFontsLoad(canvas) {
  if (!canvas || typeof canvas.getObjects !== "function") return;
  ensureTextFontsLoaded();
  const texts = canvas.getObjects().filter((o) => typeof o.text === "string" && o.fontFamily);
  const families = [...new Set(texts.map((o) => primaryFontFamily(o.fontFamily)))].filter((f) =>
    TEXT_FONTS.some((t) => t.family === f && f !== "Arial")
  );
  if (!families.length) return;
  Promise.all(families.map(loadTextFont)).then(() => {
    // Fabric caches glyph widths per family; drop what it measured with the fallback font.
    if (fabric.util && typeof fabric.util.clearFabricFontCache === "function") fabric.util.clearFabricFontCache();
    texts.forEach((o) => {
      if (typeof o.initDimensions === "function") o.initDimensions();
      o.dirty = true;
      o.setCoords();
    });
    canvas.requestRenderAll();
  });
}
